/**
 * Main facet filtering form component
 * Handles all filter interactions, AJAX updates, and URL management
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // Only respond to inputs within these selectors
    const inputWhitelist = [".facet", ".collection-actions-wrapper"];

    // Ignore inputs from these selectors
    const inputBlacklist = [".collection-actions-compare-toggle"];

    // Price range inputs trigger slower (500ms) to avoid excessive requests while typing
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    // Other non-checkbox inputs trigger faster (250ms) with immediate execution
    this.debouncedOnSubmitReduced = debounce(
      (event) => {
        this.onSubmitHandler(event);
      },
      250,
      true,
    );

    // Standard input events are used for price/sort controls.
    this.addEventListener("input", (event) => {
      if (
        !inputWhitelist.some((selector) =>
          !!event.target.closest(selector),
        ) ||
        inputBlacklist.some((selector) =>
          !!event.target.closest(selector),
        )
      ) {
        return false;
      }

      // Checkboxes are handled separately through the "change" event below.
      if (
        event.target.matches('input[type="checkbox"]') &&
        event.target.closest(".facet")
      ) {
        return;
      }

      if (event.target.closest("price-range")) {
        this.debouncedOnSubmit(event);
      } else {
        this.debouncedOnSubmitReduced(event);
      }
    });

    // Use "change" for checkbox filters so FormData always sees the
    // final checked/unchecked state of the control.
    this.addEventListener("change", (event) => {
      if (
        !inputWhitelist.some((selector) =>
          !!event.target.closest(selector),
        ) ||
        inputBlacklist.some((selector) =>
          !!event.target.closest(selector),
        )
      ) {
        return false;
      }

      if (
        event.target.matches('input[type="checkbox"]') &&
        event.target.closest(".facet")
      ) {
        this.onSubmitHandler(event);
      }
    });
  }

  /**
   * Set up browser back/forward button handling
   */
  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;

      if (searchParams === FacetFiltersForm.searchParamsPrev) {
        return;
      }

      // Back/forward is a filter-state render, so preserve the current position.
      FacetFiltersForm.renderPage(
        searchParams,
        null,
        false,
        undefined,
        true,
      );
    };

    window.addEventListener("popstate", onHistoryChange);
  }

  /**
   * Toggle disabled state of active filter remove buttons
   */
  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  /**
   * Main method to render the page with new filter/sort parameters
   */
  static renderPage(
    searchParams,
    event,
    updateURLHash = true,
    appendResults,
    preserveScroll = false,
  ) {
    // Each render gets a new identifier.
    // Older AJAX responses are ignored if a newer render already started.
    const renderId = ++FacetFiltersForm.renderId;

    // Pagination keeps its existing behavior.
    if (appendResults) {
      preserveScroll = false;
    }

    // Capture page scroll before DOM replacement.
    const scrollY = preserveScroll
      ? window.scrollY
      : null;

    // Capture internal mobile filter drawer scroll.
    const filterDrawer = preserveScroll
      ? document.querySelector(
          ".page-layout-sidebar-inner-content",
        )
      : null;

    const filterDrawerScrollTop = filterDrawer
      ? filterDrawer.scrollTop
      : null;

    // Store current params for comparison on back/forward navigation
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const countContainer =
      document.getElementById("ProductCount");

    // Add loading state to product grid
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");

    // Add loading state to product count
    if (countContainer) {
      countContainer.classList.add("loading");
    }

    // Render each required section
    sections.forEach((section) => {
      const url =
        `${window.location.pathname}?section_id=${section.section}&${searchParams}`;

      const filterDataUrl = (element) =>
        element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(
          filterDataUrl,
          event,
          preserveScroll,
          scrollY,
          filterDrawerScrollTop,
          renderId,
        );
      } else {
        FacetFiltersForm.renderSectionFromFetch(
          url,
          event,
          appendResults,
          preserveScroll,
          scrollY,
          filterDrawerScrollTop,
          renderId,
        );
      }
    });

    // Update browser URL
    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(
        searchParams,
      );
    }

    // Dispatch custom event for analytics/tracking
    if (!searchParams.includes("page")) {
      const filtersEvent =
        new CustomEvent(
          "collection:product-filters",
          {
            detail: {
              filters:
                deserializeSearchParams(
                  searchParams,
                ),
            },
          },
        );

      document.dispatchEvent(
        filtersEvent,
      );
    }
  }

  /**
   * Fetch section HTML from server via AJAX
   */
  static renderSectionFromFetch(
    url,
    event,
    appendResults,
    preserveScroll = false,
    scrollY = null,
    filterDrawerScrollTop = null,
    renderId,
  ) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        // Ignore stale responses.
        if (
          renderId !==
          FacetFiltersForm.renderId
        ) {
          return;
        }

        const html = responseText;

        // Cache response
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            url,
          },
        ];

        // Update all relevant page sections
        FacetFiltersForm.renderCollectionActions(
          html,
        );

        FacetFiltersForm.renderFilters(
          html,
          event,
        );

        FacetFiltersForm.renderProductGridContainer(
          html,
          appendResults,
        );

        FacetFiltersForm.renderProductCount(
          html,
        );

        // Restore scroll after DOM reflow
        if (
          preserveScroll &&
          scrollY !== null
        ) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (
                renderId !==
                FacetFiltersForm.renderId
              ) {
                return;
              }

              const scrollingElement =
                document.scrollingElement ||
                document.documentElement;

              scrollingElement.scrollTop =
                scrollY;

              if (
                filterDrawerScrollTop !==
                null
              ) {
                const currentFilterDrawer =
                  document.querySelector(
                    ".page-layout-sidebar-inner-content",
                  );

                if (currentFilterDrawer) {
                  currentFilterDrawer.scrollTop =
                    filterDrawerScrollTop;
                }
              }
            });
          });
        }
      })
      .catch((error) => {
        console.error(
          "Facet filter request failed:",
          error,
        );
      });
  }

  /**
   * Render cached section
   */
  static renderSectionFromCache(
    filterDataUrl,
    event,
    preserveScroll = false,
    scrollY = null,
    filterDrawerScrollTop = null,
    renderId,
  ) {
    // Ignore stale cached renders
    if (
      renderId !==
      FacetFiltersForm.renderId
    ) {
      return;
    }

    const html =
      FacetFiltersForm.filterData.find(
        filterDataUrl,
      ).html;

    // Update all relevant page sections
    FacetFiltersForm.renderCollectionActions(
      html,
    );

    FacetFiltersForm.renderFilters(
      html,
      event,
    );

    FacetFiltersForm.renderProductGridContainer(
      html,
    );

    FacetFiltersForm.renderProductCount(
      html,
    );

    // Restore scroll after DOM reflow
    if (
      preserveScroll &&
      scrollY !== null
    ) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (
            renderId !==
            FacetFiltersForm.renderId
          ) {
            return;
          }

          const scrollingElement =
            document.scrollingElement ||
            document.documentElement;

          scrollingElement.scrollTop =
            scrollY;

          if (
            filterDrawerScrollTop !==
            null
          ) {
            const currentFilterDrawer =
              document.querySelector(
                ".page-layout-sidebar-inner-content",
              );

            if (currentFilterDrawer) {
              currentFilterDrawer.scrollTop =
                filterDrawerScrollTop;
            }
          }
        });
      });
    }
  }

  /**
   * Update the product grid with new products
   */
  static renderProductGridContainer(
    html,
    appendResults,
  ) {
    const container =
      document.getElementById(
        "ProductGridContainer",
      );

    const parsedDom =
      new DOMParser().parseFromString(
        html,
        "text/html",
      );

    // For "load more" pagination, preserve existing products.
    if (appendResults) {
      parsedDom
        .getElementById(
          "product-grid",
        )
        .prepend(
          ...[
            ...document.getElementById(
              "product-grid",
            ).childNodes,
          ],
        );
    }

    // Replace grid contents.
    container.innerHTML =
      parsedDom.getElementById(
        "ProductGridContainer",
      ).innerHTML;
  }

  /**
   * Update product count
   */
  static renderProductCount(html) {
    const count =
      new DOMParser()
        .parseFromString(
          html,
          "text/html",
        )
        .getElementById(
          "ProductCount",
        )?.innerHTML;

    const container =
      document.getElementById(
        "ProductCount",
      );

    if (!container) {
      return;
    }

    container.innerHTML =
      count;

    container.classList.remove(
      "loading",
    );
  }

  /**
   * Update collection actions
   */
  static renderCollectionActions(
    html,
  ) {
    const container =
      document.getElementById(
        "CollectionActions",
      );

    const parsedDom =
      new DOMParser().parseFromString(
        html,
        "text/html",
      );

    if (!container) {
      return;
    }

    const parsedContainerActions =
      parsedDom.getElementById(
        "CollectionActions",
      );

    if (!parsedContainerActions) {
      return;
    }

    // Preserve sticky state
    if (
      container.classList.contains(
        "is-stuck",
      )
    ) {
      parsedContainerActions.classList.add(
        "is-stuck",
      );
    }

    // Update classes
    container.classList =
      parsedContainerActions.classList;

    // Update main collection actions
    const parsedCollectionActionsMain =
      parsedContainerActions.querySelector(
        ".collection-actions-main, .section-main-search",
      );

    const collectionActionsMain =
      container.querySelector(
        ".collection-actions-main",
      );

    if (
      parsedCollectionActionsMain &&
      collectionActionsMain
    ) {
      collectionActionsMain.classList =
        parsedCollectionActionsMain.classList;

      collectionActionsMain.innerHTML =
        parsedCollectionActionsMain.innerHTML;
    }
  }

  /**
   * Update filter sidebar
   * Preserves expanded/collapsed state.
   */
  static renderFilters(
    html,
    event,
  ) {
    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        "text/html",
      );

    const oldFacetElements =
      document.querySelectorAll(
        "#FacetFiltersForm .js-filter",
      );

    const newFacetElements =
      parsedHTML.querySelectorAll(
        "#FacetFiltersForm .js-filter",
      );

    // Save expanded facets
    const expandedFacets = [];
    const expandedFacetValues = [];

    document
      .querySelectorAll(
        '#FacetFiltersForm .js-filter collapsible-expandable[expanded="true"]',
      )
      ?.forEach((node) => {
        const filterName =
          node.closest(
            ".js-filter",
          ).dataset.name;

        expandedFacets.push(
          filterName,
        );

        if (!node.listCollapsed) {
          expandedFacetValues.push(
            filterName,
          );
        }
      });

    // Save focused element
    const facetsContainer =
      document.querySelector(
        "#FacetFiltersForm .page-layout-sidebar-inner-content",
      );

    const focusedElement =
      document.activeElement;

    let focusedFilterId =
      null;

    if (
      facetsContainer?.contains(
        focusedElement,
      )
    ) {
      focusedFilterId =
        focusedElement.id;
    }

    // Preserve sidebar height
    if (facetsContainer) {
      const previousHeight =
        facetsContainer.getBoundingClientRect()
          .height;

      facetsContainer.style.minHeight =
        `${previousHeight}px`;
    }

        // Replace facets
    oldFacetElements.forEach(
      (facet) => facet.remove(),
    );

    newFacetElements.forEach(
      (element) => {
        const filterName =
          element.dataset.name;

        facetsContainer.append(
          element,
        );

        const collapsible =
          element.querySelector(
            "collapsible-expandable",
          );

        const isExpanded =
          expandedFacets.includes(
            filterName,
          );

        const hasExpandedValues =
          expandedFacetValues.includes(
            filterName,
          );

        if (collapsible) {
          if (isExpanded) {
            collapsible.expand();
          } else {
            collapsible.collapse();
          }

          if (hasExpandedValues) {
            collapsible.expandList();
          } else {
            collapsible.collapseList();
          }
        }
      },
    );

    // Preserve Product Type and Vendor checkbox state specifically on /collections/ALL.
    // The current URL contains the complete active filter combination.
    if (
      /\/collections\/all\/?$/i.test(
        window.location.pathname,
      )
    ) {
      const params =
        new URLSearchParams(
          window.location.search,
        );

      const activeProductTypes =
        new Set(
          params.getAll(
            "filter.p.product_type",
          ),
        );

      const activeVendors =
        new Set(
          params.getAll(
            "filter.p.vendor",
          ),
        );

      facetsContainer
        .querySelectorAll(
          'input[type="checkbox"]',
        )
        .forEach(
          (checkbox) => {
            if (
              checkbox.name ===
              "filter.p.product_type"
            ) {
              checkbox.checked =
                activeProductTypes.has(
                  checkbox.value,
                );
            }

            if (
              checkbox.name ===
              "filter.p.vendor"
            ) {
              checkbox.checked =
                activeVendors.has(
                  checkbox.value,
                );
            }
          },
        );
    }

    // Restore focus without scrolling
    if (focusedFilterId) {
      document
        .getElementById(
          focusedFilterId,
        )
        ?.focus({
          preventScroll: true,
        });
    }

    // Remove temporary min-height
    if (facetsContainer) {
      requestAnimationFrame(() => {
        facetsContainer.style.minHeight =
          "";
      });
    }

    // No automatic filter scroll.
    // The page scroll is handled centrally by renderPage/renderSection.

    // Update active filter chips
    FacetFiltersForm.renderActiveFacets(
      parsedHTML,
    );
  }

  /**
   * Update active filters
   */
  static renderActiveFacets(
    html,
  ) {
    const activeFacetElementSelectors =
      [
        ".collection-actions-filters",
      ];

    activeFacetElementSelectors.forEach(
      (selector) => {
        const activeFacetsElement =
          html.querySelector(
            selector,
          );

        const currentElement =
          document.querySelector(
            selector,
          );

        if (
          !activeFacetsElement ||
          !currentElement
        ) {
          return;
        }

        currentElement.innerHTML =
          activeFacetsElement.innerHTML;
      },
    );

    // Re-enable remove buttons
    FacetFiltersForm.toggleActiveFacets(
      false,
    );
  }

  /**
   * Update browser URL
   */
  static updateURLHash(
    searchParams,
    replace,
  ) {
    if (replace) {
      history.replaceState(
        { searchParams },
        "",
        `${window.location.pathname}${
          searchParams &&
          "?".concat(
            searchParams,
          )
        }`,
      );
    } else {
      history.pushState(
        { searchParams },
        "",
        `${window.location.pathname}${
          searchParams &&
          "?".concat(
            searchParams,
          )
        }`,
      );
    }
  }

  /**
   * Get collection section
   */
  static getSections() {
    return [
      {
        section:
          document.querySelector(
            "#product-grid, #product-rows",
          ).dataset.id,
      },
    ];
  }

  /**
   * Handle filter input/change
   */
  onSubmitHandler(
    event,
  ) {
    event.preventDefault();

    const formData =
      new FormData(
        document.getElementById(
          "FacetsFilterForm",
        ),
      );

    const searchParams =
      new URLSearchParams(
        formData,
      ).toString();

    // Checkbox filters preserve scroll.
    const isFacetCheckbox =
      event?.target instanceof Element &&
      event.target.matches(
        'input[type="checkbox"]',
      ) &&
      event.target.closest(
        ".facet",
      );

    const isFacetRemove =
      event?.currentTarget instanceof Element &&
      event.currentTarget.closest(
        ".js-facet-remove",
      );

    FacetFiltersForm.renderPage(
      searchParams,
      event,
      true,
      undefined,
      isFacetCheckbox ||
        !!isFacetRemove,
    );
  }

  /**
   * Handle active filter removal
   */
  onActiveFilterClick(
    event,
  ) {
    event.preventDefault();

    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf(
        "?",
      ) == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf(
              "?",
            ) + 1,
          );

    // Preserve scroll when removing a filter.
    FacetFiltersForm.renderPage(
      url,
      event,
      true,
      undefined,
      true,
    );
  }

  /**
   * Handle AJAX pagination
   */
  onAjaxPagination(
    event,
  ) {
    event.preventDefault();

    const next =
      event.currentTarget;

    const url =
      next.href.indexOf("?") == -1
        ? ""
        : next.href.slice(
            next.href.indexOf(
              "?",
            ) + 1,
          );

    // Pagination uses appendResults=true.
    FacetFiltersForm.renderPage(
      url,
      event,
      true,
      false,
    );

    // Preserve existing pagination scroll behavior.
    document
      .getElementById(
        "MainContent",
      )
      .scrollIntoView(true);

    // Dispatch pagination event
    const params =
      new URLSearchParams(
        url,
      );

    const customEvent =
      new CustomEvent(
        "pagination:page-change",
        {
          detail: {
            page: Number(
              params.get(
                "page",
              ),
            ),
          },
        },
      );

    document.dispatchEvent(
      customEvent,
    );
  }
}

// Static state
FacetFiltersForm.filterData = [];

FacetFiltersForm.searchParamsInitial =
  window.location.search.slice(1);

FacetFiltersForm.searchParamsPrev =
  window.location.search.slice(1);

// Latest render identifier.
// Prevents older AJAX responses from overwriting newer filter state.
FacetFiltersForm.renderId = 0;

// Register custom element
customElements.define(
  "facet-filters-form",
  FacetFiltersForm,
);

// Set browser history listeners
FacetFiltersForm.setListeners();

/**
 * Price range filter component
 */
class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll(
      "input",
    ).forEach((element) =>
      element.addEventListener(
        "change",
        this.onRangeChange.bind(
          this,
        ),
      ),
    );

    this.setMinAndMaxValues();
  }

  onRangeChange(
    event,
  ) {
    this.adjustToValidValues(
      event.currentTarget,
    );

    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs =
      this.querySelectorAll(
        "input",
      );

    const minInput =
      inputs[0];

    const maxInput =
      inputs[1];

    if (maxInput.value) {
      minInput.setAttribute(
        "max",
        maxInput.value,
      );
    }

    if (minInput.value) {
      maxInput.setAttribute(
        "min",
        minInput.value,
      );
    }

    if (
      minInput.value === ""
    ) {
      maxInput.setAttribute(
        "min",
        0,
      );
    }

    if (
      maxInput.value === ""
    ) {
      maxInput.setAttribute(
        "max",
        maxInput.getAttribute(
          "max",
        ),
      );
    }
  }

  adjustToValidValues(
    input,
  ) {
    const value =
      Number(input.value);

    const min =
      Number(
        input.getAttribute(
          "min",
        ),
      );

    const max =
      Number(
        input.getAttribute(
          "max",
        ),
      );

    if (value < min) {
      input.value =
        min;
    }

    if (value > max) {
      input.value =
        max;
    }
  }
}

customElements.define(
  "price-range",
  PriceRange,
);

/**
 * Facet remove button component
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();

    this.querySelector(
      "a",
    ).addEventListener(
      "click",
      (event) => {
        event.preventDefault();

        const form =
          this.closest(
            "facet-filters-form",
          ) ||
          document.querySelector(
            "facet-filters-form",
          );

        form.onActiveFilterClick(
          event,
        );
      },
    );
  }
}

customElements.define(
  "facet-remove",
  FacetRemove,
);

/**
 * AJAX pagination component
 */
class AjaxPaginate extends HTMLElement {
  constructor() {
    super();

    const form =
      this.closest(
        "facet-filters-form",
      ) ||
      document.querySelector(
        "facet-filters-form",
      );

    const buttons =
      this.querySelectorAll(
        "a",
      );

    buttons.forEach(
      (button) => {
        button.addEventListener(
          "click",
          (event) => {
            event.preventDefault();
            form.onAjaxPagination(
              event,
            );
          },
        );
      },
    );
  }
}

customElements.define(
  "ajax-paginate",
  AjaxPaginate,
);

/**
 * Sticky collection actions bar
 */
class StickyCollectionActions extends HTMLElement {
  constructor() {
    super();

    this.sticky =
      this.children[0];

    this.createObserver();
  }

  createObserver() {
    const observer =
      new IntersectionObserver(
        ([entry]) => {
          entry.target.classList.toggle(
            "is-stuck",
            entry.boundingClientRect
              .top < 0,
          );
        },
        {
          threshold: 1,
        },
      );

    observer.observe(
      this.sticky,
    );
  }
}

customElements.define(
  "sticky-collection-actions",
  StickyCollectionActions,
);

/**
 * Utility function: Convert URL query string to object
 */
const deserializeSearchParams = (
  queryString,
) => {
  const params =
    new URLSearchParams(
      queryString,
    );

  const result = {};

  for (
    const [
      key,
      value,
    ] of params.entries()
  ) {
    if (result[key]) {
      result[key] =
        `${result[key]},${value}`;
    } else if (value) {
      result[key] =
        value;
    }
  }

  return result;
};
